<script lang="ts">
  import { LEFT_SIDEPANEL_WIDTH_PX } from "$utils/consts";
  import { goto } from "$app/navigation";
  import { alerts } from "$stores/alert";
  import { SidePanelState } from "app/models/shared-types";
  import { auth, isUserAuthed, session, user } from "$stores/auth";
  import WorkspaceHeader from "$components/community/WorkspaceHeader.svelte";
  import GlobalNavItem from "./GlobalNavItem.svelte";
  import Avatar from "$components/+shared/Avatar/Avatar.svelte";
  import Dropdown from "$components/+design-system/Dropdown/Dropdown.svelte";
  import Link from "$components/+design-system/Dropdown/fields/Link.svelte";
  import Action from "$components/+design-system/Dropdown/fields/Action.svelte";
  import SearchButton from "$components/ai/search/SearchButton.svelte";
  import MaintenanceBanner from "$components/MaintenanceBanner/MaintenanceBanner.svelte";
  import { getMediaQuery } from "$utils/mediaQuery";
  import IconButton from "$components/+design-system/IconButton/IconButton.svelte";
  import { isLeftPanelCollapsed } from "$stores/userPreferences";
  import autoAnimate from "@formkit/auto-animate";
  import {
    basePlatformUserHomeUrl,
    changelogRootURL,
    communityRootUrl,
    communitySettingsURL,
    contentLibraryUrl,
    docRootURL,
    sidebarRouteIds,
    userProfileUrl,
  } from "$utils/urls";
  import {
    community,
    contentLibraryTitle,
    trustedWorkspaceFlag,
    isChangelogEnabled,
    isCommunityEnabled,
    isDocumentsEnabled,
    changelogFlag,
    knowledgeBaseUIFlag,
  } from "$stores/community";
  import { communitySlug, isContentLibraryEnabled } from "$stores/community";
  import { page } from "$app/stores";
  import { externalLinks } from "$stores/community";
  import {
    canAddDocument,
    canEditCommunitySettings,
    canInviteMembers,
    isCommunityMember,
  } from "$stores/permissions";
  import Modal from "$components/+shared/Modal/Modal.svelte";
  import IconButtonTrigger from "$components/+design-system/Dropdown/fields/IconButtonTrigger.svelte";
  import CommunityJoinCard from "$components/community/CommunityJoinCard/CommunityJoinCard.svelte";
  import CommunityNotificationPreferences from "app/components/community/NotificationPreferences.svelte";
  import DialogueModal from "$components/+shared/Modal/DialogueModal.svelte";
  import api from "api";
  import {
    Doc,
    LeftAside,
    HashTag,
    Library,
    Dots,
    ExternalLink,
    Cog,
    Bell,
    PlusPerson,
    CoverFilled,
    LA,
  } from "$components/+shared/OVIcon";
  import { getStores } from "app/stores";
  import { showAdminPanel } from "app/stores/adminPanel";
  import { MAINTENANCE_MODE_ON } from "$utils/consts";
  import DropdownSection from "$components/+design-system/Dropdown/fields/DropdownSection.svelte";
  import Separator from "app/components/+shared/Separator/Separator.svelte";

  const { userPreferences } = getStores();
  let isJoinCommunityModalOpen = false;
  let isLeaveCommunityModalOpen = false;

  const isDesktop = getMediaQuery("desktop");
  const queryParameters = $page.url.searchParams;

  $: hasSidebar = sidebarRouteIds.some((routeId) => $page.route?.id?.includes(routeId));
  $: workspaceHeaderWidth = $isLeftPanelCollapsed ? "auto" : LEFT_SIDEPANEL_WIDTH_PX - 18 + "px";
  $: isNotificationPreferencesModalOpen = !!(
    $isUserAuthed && queryParameters.get("channel-preferences")
  );

  $: hasExternalLinks = !!$externalLinks?.length;
  $: hasCommunitySettings = $canEditCommunitySettings;
  $: hasCommunityActions = $isCommunityEnabled && ($isCommunityMember || $canInviteMembers);
  $: hasUserActions = $auth.isAuthed;

  // TODO: Since Svelte treats content inside {#if} blocks as part of the default slot,
  // we can't detect from within the Dropdown component if it actually has content.
  // This limitation may be resolved in Svelte 5.
  $: hasDropdownContent =
    hasExternalLinks || hasCommunitySettings || hasCommunityActions || hasUserActions;
</script>

<div>
  {#if MAINTENANCE_MODE_ON && $auth.isAuthed}
    <MaintenanceBanner />
  {/if}
  <!-- a11y: id given to nav so screen readers can differentiate between multiple nav sections (ie when the admin sidebar is shown) -->
  <nav
    id="global-header-nav"
    class:rounded-t-md={$showAdminPanel}
    class="flex h-[56px] w-full items-center overflow-hidden px-2"
    use:autoAnimate={{ duration: $isDesktop ? 150 : 75 }}
  >
    {#if hasSidebar && $isLeftPanelCollapsed}
      <span class="mr-1">
        <IconButton
          variant="tertiary"
          icon={LeftAside}
          size="large"
          a11yLabel="Open sidebar [⇧S]"
          customIconSize="h-[18px]"
          on:click={() => userPreferences.setLeftPanelExpansion(SidePanelState.Expanded)}
        />
      </span>
    {/if}

    <div style="width: {workspaceHeaderWidth}; max-width: {LEFT_SIDEPANEL_WIDTH_PX}px">
      <WorkspaceHeader />
    </div>

    {#if $isLeftPanelCollapsed}
      <div class="ml-2 hidden h-[28px] w-[1px] bg-gray-300 sm:block dark:bg-gray-600" />
    {/if}

    <ul class="ml-auto flex shrink items-center gap-3 sm:ml-3">
      {#if $isDocumentsEnabled && ($community.docsVisible || $canAddDocument) && $knowledgeBaseUIFlag}
        <li>
          <GlobalNavItem
            title={$community.documentsName || "Knowledge Base"}
            icon={Doc}
            routeId="/[community=slug]/(knowledge-base)"
            path={docRootURL({ communitySlug: $communitySlug })}
          />
        </li>
      {/if}
      {#if $isChangelogEnabled && $community.changelogVisible && $changelogFlag}
        <li>
          <GlobalNavItem
            title="Changelog"
            icon={CoverFilled}
            routeId="/[community=slug]/changelog"
            path={changelogRootURL({ communitySlug: $communitySlug })}
          />
        </li>
      {/if}
      {#if $isCommunityEnabled}
        <li>
          <GlobalNavItem
            title={$community.communitySectionName || "Community"}
            icon={HashTag}
            routeId={"/[community=slug]/forum"}
            path={communityRootUrl({
              communitySlug: $communitySlug,
            })}
          />
        </li>
      {/if}
      {#if $isContentLibraryEnabled}
        <li>
          <GlobalNavItem
            title={$contentLibraryTitle}
            icon={Library}
            routeId={"/[community=slug]/library"}
            path={contentLibraryUrl({
              communitySlug: $communitySlug,
            })}
          />
        </li>
      {/if}

      {#if $isDesktop}
        {#each $externalLinks as { url, tag, image }}
          <li>
            <GlobalNavItem
              title={tag}
              avatar={image || undefined}
              path={url}
              openInNewTab
              hideOnMobile
            />
          </li>
        {/each}
      {/if}

      {#if !$isDesktop && hasDropdownContent}
        <Dropdown>
          <IconButtonTrigger
            slot="trigger"
            size="large"
            icon={Dots}
            variant="secondary"
            a11yLabel="More options"
            let:active
            {active}
          />

          {#if hasExternalLinks}
            <DropdownSection>
              {#each $externalLinks as { url, tag, image }}
                <Link href={url} title={tag} avatar={image || undefined} icon={ExternalLink} />
              {/each}
            </DropdownSection>
          {/if}

          {#if hasCommunitySettings}
            <DropdownSection>
              <Link
                href={communitySettingsURL({
                  communitySlug: $communitySlug,
                  section: "general",
                  subSection: "overview",
                })}
                title="Workspace settings"
                icon={Cog}
              />
            </DropdownSection>
          {/if}

          {#if hasCommunityActions}
            <DropdownSection>
              {#if $isCommunityMember}
                <Action
                  title="Notifications"
                  icon={Bell}
                  on:click={() => (isNotificationPreferencesModalOpen = true)}
                />
              {/if}
              {#if $canInviteMembers && $trustedWorkspaceFlag}
                <Action
                  title="Invite member"
                  icon={PlusPerson}
                  on:click={() => (isJoinCommunityModalOpen = true)}
                />
              {/if}
              {#if $isCommunityMember}
                <Action
                  title="Leave community"
                  icon={LA}
                  danger
                  on:click={() => (isLeaveCommunityModalOpen = true)}
                />
              {/if}
            </DropdownSection>
          {/if}

          {#if hasUserActions}
            <DropdownSection>
              <Link href={userProfileUrl()} title="User settings" icon={Cog} />
              <Action danger title="Log out" icon={LA} on:click={() => session.signout()} />
            </DropdownSection>
          {/if}
        </Dropdown>
      {/if}
    </ul>

    <div class="ml-3 flex items-center gap-3 sm:ml-auto">
      <SearchButton />

      {#if $isDesktop && $auth.isAuthed && !$showAdminPanel}
        <Dropdown>
          <div slot="trigger" aria-label="user-menu">
            <Avatar
              mainImage={$session?.user?.profileImage}
              label={$session?.user?.handle?.charAt(0)}
              size="size-[36px]"
            />
          </div>

          <Link href={userProfileUrl()} title="User settings" icon={Cog} />
          <Action danger title="Log out" icon={LA} on:click={() => session.signout()} />
        </Dropdown>
      {/if}
    </div>
  </nav>
  <Separator />
</div>

<Modal bind:open={isJoinCommunityModalOpen} maxWidth={"max-w-[500px]"} withCloseIcon>
  <CommunityJoinCard />
</Modal>

<CommunityNotificationPreferences bind:open={isNotificationPreferencesModalOpen} />

<DialogueModal
  title={"Leave community"}
  message={`Are you sure you want to leave ${$community.name}?`}
  bind:open={isLeaveCommunityModalOpen}
  confirmLabel={"Leave"}
  danger
  on:confirm={async () => {
    try {
      if ($communitySlug && $user) {
        await api.communitiesMembersDestroy({
          communitySlug: $communitySlug,
          uuid: $user.uuid,
        });
        goto(basePlatformUserHomeUrl(), { state: { leftCommunity: $community.name } });
      }
    } catch (e) {
      alerts.error(`Unable to leave ${$communitySlug} community`);
    }
  }}
/>
